import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@copro/api";

type UploadResult = {
  fileId: string;
  viewUrl: string;
  thumbnailUrl: string;
};

type UploadParams = {
  files: File[];
  spaceId: string;
  subjectId?: string;
  folderName?: string;
  convertToDoc?: boolean;
};

export async function uploadFilesToGoogleDrive(params: UploadParams): Promise<UploadResult[]> {
  const formData = new FormData();
  for (const file of params.files) {
    formData.append('files', file);
  }
  formData.append('spaceId', params.spaceId);
  if (params.subjectId) {
    formData.append('subjectId', params.subjectId);
  }
  if (params.folderName) {
    formData.append('folderName', params.folderName);
  }
  if (params.convertToDoc) {
    formData.append('convertToDoc', 'true');
  }

  const { data, error } = await apiClient
    .functions
    .invoke('upload-to-drive', {
      body: formData,
    });

  if (error) {
    let errorMessage = error.message;

    // Supabase FunctionsHttpError stores the raw Response in error.context
    const errorWithContext = error as unknown as { context?: unknown };
    if (errorWithContext.context instanceof Response) {
      try {
        const body = (await errorWithContext.context.clone().json()) as { message?: string };
        if (body?.message) {
          errorMessage = body.message;
        }
      } catch {
        // Fallback to error.message if json parsing fails
      }
    }

    if (
      errorMessage.includes('No Google Drive connected') ||
      errorMessage.includes('DriveNotConnected')
    ) {
      throw new Error("Aucun compte Google Drive n'est connecté pour cette copropriété.");
    }
    if (
      errorMessage.includes('token refresh failed') ||
      errorMessage.includes('invalid_grant')
    ) {
      throw new Error(
        'La connexion à Google Drive a expiré. Veuillez reconnecter votre compte dans les paramètres.'
      );
    }
    if (errorMessage.includes('Drive folder creation failed')) {
      throw new Error("Impossible de créer le dossier dans Google Drive.");
    }
    if (errorMessage.includes('Drive upload failed')) {
      throw new Error("Échec de l'enregistrement du fichier dans Google Drive.");
    }
    if (errorMessage.includes('non-2xx status code')) {
      throw new Error("Erreur de communication avec Google Drive lors de l'envoi du fichier.");
    }

    throw new Error(errorMessage);
  }

  return data as UploadResult[];
}

export default function useUploadFilesToGoogleDrive() {
  return useMutation({
    mutationFn: uploadFilesToGoogleDrive,
  });
}
